import React, { useState, useEffect, useCallback } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Modal } from '../../components/ui';
import { LEAD_SOURCES, PROJECT_NATURES } from '../../lib/statusMaps';
import { formatZARExact } from '../../lib/format';
import DirectorIdLookup from './DirectorIdLookup';
import DirectorExcelUpload from './DirectorExcelUpload';
import EndClientPicker from './EndClientPicker';

const STEPS = [
  { id: 1, label: 'Client' },
  { id: 2, label: 'Directors' },
  { id: 3, label: 'Terms' },
  { id: 4, label: 'Review' },
];

const EMPTY = {
  client_company_name: '',
  client_registration_number: '',
  client_contact_person: '',
  client_phone: '',
  client_email: '',
  client_address: '',
  client_bank: '',
  lead_source: '',
  directors: [{ name: '', id_number: '' }],
  service_type: 'po_funding',
  end_client_name: '',
  supplier_name: '',
  po_number: '',
  po_value: '',
  invoice_number: '',
  invoice_value: '',
  supplier_quote: '',
  capital_required: '',
  client_requested_amount: '',
  expected_repayment_date: '',
  po_invoice_issue_date: '',
  project_type: '',
  deal_notes: '',
};

export default function NewDealWizardModal({ open, onClose, onNavigate, showToast }) {
  const [step, setStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [calcs, setCalcs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState([]);
  const [searching, setSearching] = useState(false);

  const isID = form.service_type === 'invoice_discounting';
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const setDirector = (idx, key, val) => {
    setForm(p => {
      const directors = [...p.directors];
      directors[idx] = { ...directors[idx], [key]: val };
      return { ...p, directors };
    });
  };

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setMaxStep(1);
    setForm(EMPTY);
    setCalcs(null);
    setError('');
    setExisting([]);
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const name = form.client_company_name.trim();
    if (name.length < 2) { setExisting([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      dealMgmt.search(name)
        .then(res => setExisting(res.deals || res.results || []))
        .catch(() => setExisting([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [open, form.client_company_name]);

  const runCalc = useCallback(async () => {
    try {
      const res = await dealMgmt.calculate({
        service_type: form.service_type,
        po_value: form.po_value,
        supplier_quote: form.supplier_quote,
        capital_required: form.capital_required,
        invoice_value: form.invoice_value,
        client_requested_amount: form.client_requested_amount || form.capital_required,
      });
      setCalcs(res.calculations);
      if (res.calculations?.above_75_limit) {
        setError('Requested amount exceeds the maximum allowable 75% advance.');
      } else {
        setError('');
      }
    } catch (_) { /* ignore while typing */ }
  }, [form]);

  useEffect(() => {
    if (!open || step < 3) return;
    const t = setTimeout(runCalc, 300);
    return () => clearTimeout(t);
  }, [
    open, step, form.po_value, form.supplier_quote, form.invoice_value,
    form.client_requested_amount, form.capital_required, form.service_type, runCalc,
  ]);

  const validateStep = (s) => {
    if (s === 1) {
      if (!form.client_company_name.trim()) return 'Client company name is required';
      return '';
    }
    if (s === 2) {
      const primary = form.directors[0];
      if (!primary?.name?.trim()) return 'At least one director / representative name is required';
      return '';
    }
    if (s === 3) {
      if (calcs?.above_75_limit) return 'Requested amount exceeds the maximum allowable 75% advance.';
      if (!isID && !form.capital_required && !form.po_value) {
        return 'Enter PO value / capital required';
      }
      if (isID && !form.invoice_value) return 'Invoice value is required';
      return '';
    }
    return '';
  };

  const goNext = () => {
    const msg = validateStep(step);
    if (msg) { setError(msg); return; }
    setError('');
    setStep(s => {
      const next = Math.min(4, s + 1);
      setMaxStep(m => Math.max(m, next));
      return next;
    });
  };

  const goBack = () => {
    setError('');
    setStep(s => Math.max(1, s - 1));
  };

  const goToStep = (target) => {
    if (target < 1 || target > 4 || target > maxStep || target === step || saving) return;
    // Only allow jumping back, or forward within already visited steps after validating current
    if (target > step) {
      const msg = validateStep(step);
      if (msg) { setError(msg); return; }
    }
    setError('');
    setStep(target);
  };

  const submit = async () => {
    const msg = validateStep(3) || validateStep(1) || validateStep(2);
    if (msg) { setError(msg); return; }
    setSaving(true);
    setError('');
    try {
      const primary = form.directors[0] || {};
      const payload = {
        ...form,
        director_name: primary.name || '',
        director_id_number: primary.id_number || '',
        workflow_data: {
          directors: form.directors.filter(d => d.name || d.id_number),
        },
      };
      delete payload.directors;
      delete payload._end_client_rating;
      const res = await dealMgmt.createDeal(payload);
      showToast?.('Deal created');
      onClose?.();
      onNavigate?.('dm-deal-detail', { dmDealId: res.deal.id });
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const primary = form.directors[0] || {};

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      title="New deal"
      width={720}
    >
      <div className="deal-wizard">
        <div className="wizard-steps deal-wizard-steps">
          {STEPS.map((s) => {
            const canJump = s.id <= maxStep && !saving;
            return (
              <button
                key={s.id}
                type="button"
                className={`wizard-step ${step === s.id ? 'active' : s.id < step || s.id <= maxStep ? 'done' : ''}`}
                disabled={!canJump}
                onClick={() => goToStep(s.id)}
                title={canJump ? `Go to ${s.label}` : 'Complete previous steps first'}
              >
                {s.id}. {s.label}
              </button>
            );
          })}
        </div>

        <div className="deal-wizard-body">
          {error && (
            <div style={{
              background: 'var(--danger-bg)', color: 'var(--danger)',
              padding: 10, borderRadius: 7, marginBottom: 12, fontSize: 12,
            }}>
              {error}
            </div>
          )}

          {step === 1 && (
        <div>
          <div className="form-row fr2">
            <div>
              <label className="field-label">Client company name *</label>
              <input
                className="field-input"
                autoFocus
                value={form.client_company_name}
                onChange={e => set('client_company_name', e.target.value)}
              />
              {searching && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Searching…</div>}
              {existing.length > 0 && (
                <div style={{
                  border: '1px solid var(--border)', borderRadius: 8, marginTop: 6,
                  maxHeight: 140, overflowY: 'auto',
                }}>
                  {existing.slice(0, 6).map(d => (
                    <div
                      key={d.id}
                      style={{
                        padding: '8px 10px', fontSize: 12, cursor: 'pointer',
                        borderBottom: '1px solid var(--border)',
                      }}
                      onClick={() => {
                        setForm(p => ({
                          ...p,
                          client_company_name: d.client_company_name || p.client_company_name,
                          client_registration_number: d.client_registration_number || p.client_registration_number,
                          client_contact_person: d.client_contact_person || p.client_contact_person,
                          client_phone: d.client_phone || p.client_phone,
                          client_email: d.client_email || p.client_email,
                          client_bank: d.client_bank || p.client_bank,
                        }));
                        showToast?.(`Prefill from ${d.deal_ref}`);
                      }}
                    >
                      <strong>{d.client_company_name}</strong>
                      <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{d.deal_ref}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="field-label">Registration number</label>
              <input className="field-input" value={form.client_registration_number} onChange={e => set('client_registration_number', e.target.value)} />
            </div>
          </div>
          <div className="form-row fr2">
            <div>
              <label className="field-label">Contact person</label>
              <input className="field-input" value={form.client_contact_person} onChange={e => set('client_contact_person', e.target.value)} />
            </div>
            <div>
              <label className="field-label">Phone</label>
              <input className="field-input" value={form.client_phone} onChange={e => set('client_phone', e.target.value)} />
            </div>
          </div>
          <div className="form-row fr2">
            <div>
              <label className="field-label">Email</label>
              <input className="field-input" type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} />
            </div>
            <div>
              <label className="field-label">Lead source</label>
              <select className="field-input" value={form.lead_source} onChange={e => set('lead_source', e.target.value)}>
                <option value="">Select source…</option>
                {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row fr2">
            <div>
              <label className="field-label">Client bank</label>
              <input className="field-input" value={form.client_bank} onChange={e => set('client_bank', e.target.value)} />
            </div>
            <div>
              <label className="field-label">Address</label>
              <input className="field-input" value={form.client_address} onChange={e => set('client_address', e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <p style={{ fontSize: 12, color: 'var(--text2)', margin: '0 0 12px' }}>
            Upload a blacklist Excel if needed, then enter director IDs to check status.
          </p>
          <DirectorExcelUpload showToast={showToast} compact />
          {form.directors.map((d, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div className="form-row fr2">
                <div>
                  <label className="field-label">{i === 0 ? 'Name *' : 'Name'}</label>
                  <input className="field-input" value={d.name} onChange={e => setDirector(i, 'name', e.target.value)} />
                </div>
                <div>
                  <label className="field-label">ID number</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="field-input" value={d.id_number} onChange={e => setDirector(i, 'id_number', e.target.value)} placeholder="Checks against uploaded Excel" />
                    {form.directors.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-o btn-sm"
                        onClick={() => setForm(p => ({
                          ...p,
                          directors: p.directors.filter((_, idx) => idx !== i),
                        }))}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <DirectorIdLookup idNumber={d.id_number} />
            </div>
          ))}
          <button
            type="button"
            className="btn btn-o btn-sm"
            onClick={() => setForm(p => ({ ...p, directors: [...p.directors, { name: '', id_number: '' }] }))}
          >
            + Add director
          </button>
        </div>
      )}

      {step === 3 && (
        <div>
          <div className="form-row fr2">
            <div>
              <label className="field-label">Service type *</label>
              <select className="field-input" value={form.service_type} onChange={e => set('service_type', e.target.value)}>
                <option value="po_funding">PO Funding</option>
                <option value="invoice_discounting">Invoice Discounting</option>
              </select>
            </div>
            <EndClientPicker
              value={form.end_client_name}
              showToast={showToast}
              onChange={(name) => set('end_client_name', name)}
              onSelect={(ec) => {
                set('end_client_name', ec.name);
                if (ec.rating) setForm(p => ({ ...p, end_client_name: ec.name, _end_client_rating: ec.rating }));
              }}
            />
          </div>
          <div className="form-row fr2">
            <div>
              <label className="field-label">Supplier name</label>
              <input className="field-input" value={form.supplier_name} onChange={e => set('supplier_name', e.target.value)} />
            </div>
            <div>
              <label className="field-label">Nature of project</label>
              <select
                className="field-input"
                value={form.project_type}
                onChange={e => set('project_type', e.target.value)}
              >
                <option value="">Select nature</option>
                {PROJECT_NATURES.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {!isID && (
            <>
              <div className="form-row fr2">
                <div>
                  <label className="field-label">PO number</label>
                  <input className="field-input" value={form.po_number} onChange={e => set('po_number', e.target.value)} />
                </div>
                <div>
                  <label className="field-label">PO issue date</label>
                  <input
                    className="field-input"
                    type="date"
                    value={form.po_invoice_issue_date}
                    onChange={e => set('po_invoice_issue_date', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row fr2">
                <div>
                  <label className="field-label">PO value</label>
                  <input className="field-input" type="number" value={form.po_value} onChange={e => set('po_value', e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Supplier quote / cost</label>
                  <input className="field-input" type="number" value={form.supplier_quote} onChange={e => set('supplier_quote', e.target.value)} />
                </div>
              </div>
              <div className="form-row fr2">
                <div>
                  <label className="field-label">Capital required</label>
                  <input className="field-input" type="number" value={form.capital_required} onChange={e => set('capital_required', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {isID && (
            <>
              <div className="form-row fr2">
                <div>
                  <label className="field-label">Invoice number</label>
                  <input className="field-input" value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Invoice issue date</label>
                  <input
                    className="field-input"
                    type="date"
                    value={form.po_invoice_issue_date}
                    onChange={e => set('po_invoice_issue_date', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row fr2">
                <div>
                  <label className="field-label">Total invoice value</label>
                  <input className="field-input" type="number" value={form.invoice_value} onChange={e => set('invoice_value', e.target.value)} />
                </div>
              </div>
              <div className="form-row fr3">
                <div>
                  <label className="field-label">Max advance %</label>
                  <input className="field-input" value="75%" disabled />
                </div>
                <div>
                  <label className="field-label">Maximum advance</label>
                  <input className="field-input" value={calcs?.max_advance_amount != null ? formatZARExact(calcs.max_advance_amount) : '—'} disabled />
                </div>
                <div>
                  <label className="field-label">Client requested amount</label>
                  <input
                    className="field-input"
                    type="number"
                    value={form.client_requested_amount}
                    onChange={e => {
                      set('client_requested_amount', e.target.value);
                      set('capital_required', e.target.value);
                    }}
                  />
                </div>
              </div>
            </>
          )}

          <div className="form-row fr2">
            <div>
              <label className="field-label">Expected repayment date</label>
              <input className="field-input" type="date" value={form.expected_repayment_date} onChange={e => set('expected_repayment_date', e.target.value)} />
            </div>
            <div>
              <label className="field-label">Deal notes</label>
              <input className="field-input" value={form.deal_notes} onChange={e => set('deal_notes', e.target.value)} />
            </div>
          </div>

          {calcs && !isID && (
            <div style={{ background: 'var(--brand-light)', borderRadius: 8, padding: 12, marginTop: 4, fontSize: 12 }}>
              <strong>PO Funding calculation</strong>
              <div>
                Gross profit: {formatZARExact(calcs.gross_profit)} · Margin: {calcs.profit_margin_pct?.toFixed(2)}%
                {calcs.below_min_margin && (
                  <span style={{ color: 'var(--danger)', marginLeft: 8 }}>Below Minimum Margin (30%)</span>
                )}
              </div>
            </div>
          )}
          {isID && (
            <div style={{ background: 'var(--brand-light)', borderRadius: 8, padding: 12, marginTop: 4, fontSize: 12 }}>
              <strong>Invoice Discounting</strong> · Profit margin: Not Applicable
              {calcs?.remaining_advance != null && (
                <div>Remaining advance: {formatZARExact(calcs.remaining_advance)}</div>
              )}
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div style={{ fontSize: 13 }}>
          <div style={{ display: 'grid', gap: 10 }}>
            <ReviewRow label="Client" value={form.client_company_name} />
            <ReviewRow label="Contact" value={[form.client_contact_person, form.client_phone, form.client_email].filter(Boolean).join(' · ') || '—'} />
            <ReviewRow label="Lead source" value={form.lead_source || '—'} />
            <ReviewRow label="Director" value={[primary.name, primary.id_number].filter(Boolean).join(' · ') || '—'} />
            <ReviewRow
              label="Service"
              value={isID ? 'Invoice Discounting' : 'PO Funding'}
            />
            <ReviewRow label="End-client" value={form.end_client_name || '—'} />
            <ReviewRow label="Supplier" value={form.supplier_name || '—'} />
            {!isID && (
              <>
                <ReviewRow label="PO" value={[form.po_number, form.po_value ? formatZARExact(form.po_value) : null].filter(Boolean).join(' · ') || '—'} />
                <ReviewRow label="PO issue date" value={form.po_invoice_issue_date || '—'} />
                <ReviewRow label="Capital required" value={form.capital_required ? formatZARExact(form.capital_required) : '—'} />
                {calcs && (
                  <ReviewRow
                    label="Margin"
                    value={`${formatZARExact(calcs.gross_profit)} · ${calcs.profit_margin_pct?.toFixed(2)}%`}
                  />
                )}
              </>
            )}
            {isID && (
              <>
                <ReviewRow label="Invoice" value={[form.invoice_number, form.invoice_value ? formatZARExact(form.invoice_value) : null].filter(Boolean).join(' · ') || '—'} />
                <ReviewRow label="Invoice issue date" value={form.po_invoice_issue_date || '—'} />
                <ReviewRow label="Requested advance" value={form.client_requested_amount ? formatZARExact(form.client_requested_amount) : '—'} />
              </>
            )}
            <ReviewRow label="Repayment date" value={form.expected_repayment_date || '—'} />
            {form.deal_notes && <ReviewRow label="Notes" value={form.deal_notes} />}
          </div>
        </div>
      )}
        </div>

        <div className="deal-wizard-footer">
          <button type="button" className="btn btn-o" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 1 && (
              <button type="button" className="btn btn-o" onClick={goBack} disabled={saving}>
                ← Back
              </button>
            )}
            {step < 4 ? (
              <button type="button" className="btn btn-p" onClick={goNext} disabled={saving}>
                Continue →
              </button>
            ) : (
              <button type="button" className="btn btn-p" onClick={submit} disabled={saving}>
                {saving ? 'Creating…' : 'Create deal'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10,
      padding: '8px 0', borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ color: 'var(--text3)', fontSize: 12 }}>{label}</span>
      <strong style={{ fontWeight: 600, fontSize: 13 }}>{value}</strong>
    </div>
  );
}
